import { Status, NonClientError, ServerError } from 'hooks/requests/fetchUtils';
import useFetchApi from 'hooks/useFetchApi';
import { useState, useCallback } from 'react';
import useFmcHost from 'hooks/useFmcHost';

import useQueryParameters from 'hooks/useQueryParameters';
import useApplicationBasedOverrides from 'hooks/useApplicationBasedOverrides';

import { receiveApplicationResult, ApplicationResultType } from 'fmc-sdk';
import catchOnly from 'utils/catchOnly';

const PATH = '/api/unified_disclosure';

type UnifiedDisclosuresHook = () => [
  (params: UnifiedDisclosuresRequest) => Promise<UnifiedDisclosuresResult>,
  Status,
  boolean,
];

export interface PartialError {
  error: true;
  code?: string;
  message?: string;
}

export interface UnifiedDisclosuresResult {
  disclosureChecksum: string;
  disclosureContent: string;
}

export interface UnifiedDisclosuresRequest {
  paymentAgreementId: string;
  type:
    | 'loan-agreement'
    | 'loan-agreement-sp'
    | 'credit-score-notice'
    | 'tila'
    | 'payment-full-terms';
  mockResponse?: string;
}

export class UnifiedDisclosuresError extends Error {}

const useUnifiedDisclosures: UnifiedDisclosuresHook = () => {
  const [error, setError] = useState<boolean>(false);
  const [status, fetch] = useFetchApi();
  const { mockUD } = useQueryParameters();
  const fmcDispatch = useFmcHost();
  const overrideAttributes = useApplicationBasedOverrides();

  const unifiedDisclosuresRequest = useCallback(
    async (
      params: UnifiedDisclosuresRequest,
    ): Promise<UnifiedDisclosuresResult> => {
      setError(false);

      try {
        // TODO: Missing params include clientVariable4. I have
        // a question out to Tim to confirm whether these are still needed.
        const response = await fetch(PATH, {
          method: 'POST',
          body: {
            ...params,
            ...overrideAttributes,
            mockResponse: mockUD,
          },
        });

        if (response.ok) {
          const result: UnifiedDisclosuresResult = await response.json();
          return Promise.resolve(result);
        } else {
          // Expect this is to be a 4xx error, in which case
          // the server is expected to return an error object.
          // So we'll attempt to parse it and get at the error message,
          // or just return the entire body if it cannot be parsed.

          const body = await response.text();
          let message = body;
          try {
            message = JSON.parse(message).message;
          } catch (e) {
            /* No need to do anything */
          }

          fmcDispatch(
            receiveApplicationResult({
              result: ApplicationResultType.Error,
              callId: null,
              message,
              status: response.status,
            }),
          );

          setError(true);
          return Promise.reject(new UnifiedDisclosuresError());
        }
      } catch (e) {
        if (catchOnly(e, NonClientError)) {
          // Let FmcHost know about server and network errors.
          let status = 0;
          let message = 'NetworkError';

          if (e instanceof ServerError) {
            status = e.response.status;
            message = await e.response.text();
          }

          fmcDispatch(
            receiveApplicationResult({
              result: ApplicationResultType.Error,
              callId: null,
              status: status,
              message: message,
            }),
          );
        }
        // Re-throw so that any exceptions can be handled downstream.
        throw e;
      }
    },
    [fetch, mockUD, fmcDispatch, overrideAttributes],
  );

  return [unifiedDisclosuresRequest, status, error];
};

export default useUnifiedDisclosures;
